/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useMemo } from 'react';
import {
  AccountStatus,
  BoxExtendedProps,
  CardTypesEnum,
  costFormater,
  Enrollment,
  EnrollmentCard,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { Roles } from '@/common/constants';
import routes from '@/common/routes';
import { useHasAccess, useHistory, useSnackbar } from '@/modules/core/hooks';
import { useInvestorQuery } from '@/modules/investments/queries';
import { useGetAccountNumberQuery } from '@/modules/transaction/queries/useGetAccountNumber.query';
import colors from '@/styles/colors';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

const LABELS = {
  election: 'Election',
  availableCashBalance: 'Available cash balance',
  availableBalance: 'Available balance',
};

interface Props extends BoxExtendedProps {
  enrollment: Enrollment;
}
const isNationwideTheme = whiteLabelingConfig.key === THEMES.nationwide;
const EnrollmentCardBlock: React.FC<Props> = ({ enrollment, ...rest }) => {
  if (!enrollment) {
    return null;
  }
  const isAccountNumberMasked = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const history = useHistory();
  const { handleAddPermanentSnackbar, handleReset } = useSnackbar();

  const accountId = useMemo(() => `${enrollment.id}`, [enrollment]);
  const isAccountTypeHSA = useMemo(() => enrollment.accountType === CardTypesEnum.HSA, [enrollment]);
  const { formattedData } = useGetAccountNumberQuery(accountId, isAccountTypeHSA);

  const accountNumber = useMemo(
    () => (formattedData.accountNumber ? formattedData.accountNumber : null),
    [formattedData]
  );
  const routingNumber = useMemo(
    () => (formattedData.routingNumber ? formattedData.routingNumber : null),
    [formattedData]
  );
  const {
    data: {
      pendingTradesAmount,
      investorEnrollmentId,
      investmentPortfolioBalance,
      isAutomaticInvestmentEnabled,
    },
  } = useInvestorQuery();

  const currentType = useMemo(() => {
    return enrollment?.accountType;
  }, [enrollment]);

  const remainingRolloverDate = useMemo(() => {
    return enrollment?.remainingRolloverDate
      ? dayjs(enrollment.remainingRolloverDate).format(DEFAULT_DATE_FORMAT)
      : undefined;
  }, [enrollment?.remainingRolloverDate]);

  const isDisabled = useMemo(() => {
    return enrollment.accountStatus === AccountStatus.CLOSED;
  }, [enrollment.accountStatus]);

  const subTitleColor = useMemo(() => colors.cardTypes[currentType], [currentType]);
  const showPendingTrades = useMemo(
    () => pendingTradesAmount > 0 && investorEnrollmentId === enrollment?.id,
    [pendingTradesAmount, investorEnrollmentId, enrollment]
  );

  const handleRedirectToInvestments = useCallback(() => {
    window.scrollTo({
      top: 0,
    });
    history.push(`${routes.INVESTMENTS}?id=${accountId}`);
  }, [accountId, history]);

  return (
    <EnrollmentCard type={isNationwideTheme ? undefined : currentType} isDisabled={isDisabled} {...rest}>
      <EnrollmentCard.Icon type={currentType} />

      <EnrollmentCard.TopbarInfo>
        <EnrollmentCard.TopbarInfo.Status
          policyStatus={enrollment.enrollmentStatus}
          accountStatus={enrollment.accountStatus}
          CIPStatus={enrollment.CIPStatus}
          isCIPRequired={enrollment.isCIPRequired}
          currentType={currentType}
          showHint
        />
        <EnrollmentCard.TopbarInfo.AccountNumber
          accountNumber={accountNumber}
          routingNumber={routingNumber}
          isAccountNumberMasked={isAccountNumberMasked}
          handleAddPermanentSnackbar={handleAddPermanentSnackbar}
          handleReset={handleReset}
        />
      </EnrollmentCard.TopbarInfo>
      <EnrollmentCard.Title>{enrollment?.name || ''}</EnrollmentCard.Title>
      <EnrollmentCard.SubTitle textColor={subTitleColor}>
        {enrollment?.displayName || ''}
      </EnrollmentCard.SubTitle>
      <EnrollmentCard.Item margin={{ top: 'spacing16', bottom: 'spacing8' }}>
        <EnrollmentCard.Item.Text testId="election">{LABELS.election}</EnrollmentCard.Item.Text>
        <EnrollmentCard.Item.Amount testId="election">
          {costFormater(enrollment?.election, true)}
        </EnrollmentCard.Item.Amount>
      </EnrollmentCard.Item>
      {!!investmentPortfolioBalance && currentType === CardTypesEnum.HSA && (
        <EnrollmentCard.Item>
          <EnrollmentCard.Item.Text testId="investment_portfolio_balance">
            Investment portfolio balance
          </EnrollmentCard.Item.Text>
          <EnrollmentCard.Item.Amount
            testId="investment_portfolio_balance"
            isAutoInvestment={isAutomaticInvestmentEnabled}
            withHoverEffect
            onClick={(e) => {
              e.stopPropagation();
              return handleRedirectToInvestments();
            }}
          >
            {costFormater(investmentPortfolioBalance, true)}
          </EnrollmentCard.Item.Amount>
        </EnrollmentCard.Item>
      )}
      {showPendingTrades && (
        <EnrollmentCard.Item>
          <EnrollmentCard.Item.Text testId="pending_trade">Pending trade</EnrollmentCard.Item.Text>
          <EnrollmentCard.Item.Amount testId="pending_trade" showPendingTrades={showPendingTrades}>
            {costFormater(pendingTradesAmount, true)}
          </EnrollmentCard.Item.Amount>
        </EnrollmentCard.Item>
      )}
      <EnrollmentCard.Item bigSize margin={{ bottom: 'none' }}>
        <EnrollmentCard.Item.Text testId="available_balance">
          {currentType === CardTypesEnum.HSA ? LABELS.availableCashBalance : LABELS.availableBalance}
        </EnrollmentCard.Item.Text>
        <EnrollmentCard.Item.Amount
          testId="available_balance"
          remainingRollover={enrollment?.remainingRollover}
          remainingRolloverDate={remainingRolloverDate}
          size="2xl"
        >
          {costFormater(enrollment?.available, true)}
        </EnrollmentCard.Item.Amount>
      </EnrollmentCard.Item>
    </EnrollmentCard>
  );
};

export default EnrollmentCardBlock;
